import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaCheckCircle, FaPlay, FaStar, FaUsers, FaChartLine } from 'react-icons/fa';
import RegisterModal from '../components/RegisterModal';

// Animations
const floatAnimation = keyframes`
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(2deg); }
`;

const glowAnimation = keyframes`
    0%, 100% { box-shadow: 0 0 20px rgba(245, 102, 7, 0.3); }
    50% { box-shadow: 0 0 40px rgba(245, 102, 7, 0.6); }
`;

const slideInLeft = keyframes`
    from {
        opacity: 0;
        transform: translateX(-50px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const slideInRight = keyframes`
    from {
        opacity: 0;
        transform: translateX(50px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

// Styled Components
const HeroContainer = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, rgba(12, 12, 12, 0.8) 0%, rgba(26, 26, 26, 0.8) 50%, rgba(45, 45, 45, 0.8) 100%), url('/stadium.jpg') no-repeat center/cover;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    padding: 80px 40px;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background:
                radial-gradient(circle at 20% 80%, rgba(245, 102, 7, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(245, 102, 7, 0.05) 0%, transparent 50%);
    }

    @media (max-width: 768px) {
        padding: 60px 20px;
    }

    @media (max-width: 480px) {
        padding: 40px 15px;
    }
`;

const HeroContent = styled.div`
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    gap: 80px;
    align-items: center;
    max-width: 1400px;
    width: 100%;
    position: relative;
    z-index: 2;

    @media (max-width: 1200px) {
        grid-template-columns: 1fr;
        gap: 60px;
        text-align: center;
    }

    @media (max-width: 768px) {
        gap: 40px;
    }
`;

const TextSection = styled.div`
    animation: ${slideInLeft} 1s ease-out;
`;

const HeroTitle = styled.h1`
    font-size: 4em;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 30px;
    line-height: 1.1;
    background: linear-gradient(135deg, #ffffff 0%, #f56607 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
        font-size: 2.8em;
    }

    @media (max-width: 480px) {
        font-size: 2.2em;
    }
`;

const Subtitle = styled.p`
    font-size: 1.4em;
    color: #b0b0b0;
    margin-bottom: 50px;
    line-height: 1.6;
    max-width: 500px;

    @media (max-width: 1200px) {
        margin: 0 auto 50px;
    }

    @media (max-width: 768px) {
        font-size: 1.2em;
        max-width: 100%;
    }
`;

const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
    max-width: 500px;
    margin-bottom: 40px;

    @media (max-width: 1200px) {
        max-width: 100%;
        margin: 0 auto 40px;
    }
`;

const FeatureItem = styled.div`
    display: flex;
    align-items: center;
    padding: 20px 25px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
        transform: translateX(10px);
        background: rgba(245, 102, 7, 0.1);
        border-color: rgba(245, 102, 7, 0.3);
    }

    @media (max-width: 768px) {
        padding: 15px 20px;

        &:hover {
            transform: none;
        }
    }
`;

const CheckIcon = styled(FaCheckCircle)`
    color: #f56607;
    font-size: 1.4em;
    margin-right: 20px;
    flex-shrink: 0;

    @media (max-width: 768px) {
        font-size: 1.2em;
        margin-right: 15px;
    }
`;

const FeatureText = styled.span`
    font-size: 1.1em;
    color: #ffffff;
    font-weight: 500;

    @media (max-width: 768px) {
        font-size: 1em;
    }
`;

const CTAButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 15px;
    padding: 20px 50px;
    background: linear-gradient(135deg, #f56607 0%, #ff8c00 100%);
    color: white;
    border: none;
    border-radius: 15px;
    font-size: 1.2em;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 10px 30px rgba(245, 102, 7, 0.4);
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
        transition: 0.5s;
    }

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 40px rgba(245, 102, 7, 0.6);

        &::before {
            left: 100%;
        }
    }

    &:active {
        transform: translateY(-1px);
    }

    @media (max-width: 768px) {
        padding: 15px 30px;
        font-size: 1em;
        gap: 10px;
    }

    @media (max-width: 480px) {
        width: 100%;
        justify-content: center;
    }
`;

const PlayIcon = styled(FaPlay)`
    font-size: 0.8em;
`;

const PreviewSection = styled.div`
    animation: ${slideInRight} 1s ease-out;
    position: relative;

    @media (max-width: 1200px) {
        display: flex;
        justify-content: center;
    }
`;

const AppPreviewContainer = styled.div`
    position: relative;
    border-radius: 30px;
    width: 60%;
    overflow: hidden;
    box-shadow:
            0 30px 60px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(255, 255, 255, 0.1);
    animation: ${glowAnimation} 4s ease-in-out infinite;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-10px) scale(1.02);
        box-shadow:
                0 40px 80px rgba(0, 0, 0, 0.6),
                0 0 0 1px rgba(245, 102, 7, 0.3);
    }

    @media (max-width: 1200px) {
        width: 80%;
        max-width: 400px;
        margin: 0 auto;
    }

    @media (max-width: 768px) {
        width: 100%;
        border-radius: 20px;
    }
`;

const AppPreviewImage = styled.img`
    width: 100%;
    height: auto;
    display: block;
    border-radius: 30px;

    @media (max-width: 768px) {
        border-radius: 20px;
    }
`;

const StatsContainer = styled.div`
    display: flex;
    justify-content: left;
    gap: 30px;
    margin-top: 40px;
    flex-wrap: wrap;

    @media (max-width: 1200px) {
        justify-content: center;
    }

    @media (max-width: 768px) {
        gap: 20px;
        margin-top: 30px;
    }
`;

const StatItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    min-width: 80px;
`;

const StatIcon = styled.div`
    width: 60px;
    height: 60px;
    border-radius: 15px;
    background: rgba(245, 102, 7, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
    color: #f56607;
    font-size: 1.5em;

    @media (max-width: 768px) {
        width: 50px;
        height: 50px;
        font-size: 1.2em;
        border-radius: 12px;
    }
`;

const StatNumber = styled.div`
    font-size: 1.4em;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 5px;

    @media (max-width: 768px) {
        font-size: 1.2em;
    }
`;

const StatLabel = styled.div`
    font-size: 0.9em;
    color: #b0b0b0;
    font-weight: 500;

    @media (max-width: 768px) {
        font-size: 0.8em;
    }
`;

const Hero: React.FC = () => {
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    const features = [
        "Pravidelné podcasty s expertmi každý týždeň",
        "Detailná analýza zápasov a športových udalostí",
        "Profesionálne rozobratie tipov do predu",
        "Komplexná analýza vašich tipovačských stratégií",
        "Kombinácia krátkodobých a dlhodobých tiketov",
        "Aktívne fórum pre diskusiu a zdieľanie tipov"
    ];

    const handleCTAClick = () => {
        setIsRegisterModalOpen(true);
    };

    const handleCloseRegisterModal = () => {
        setIsRegisterModalOpen(false);
    };

    const handleRegisterSuccess = () => {
        console.log('Registration successful from Hero!');
        // You can add any post-registration logic here
        // For example: redirect to dashboard, show success message, etc.
    };

    return (
        <HeroContainer>
            <HeroContent>
                <TextSection>
                    <HeroTitle>
                        Posuňte svoj prehľad na novú úroveň.
                    </HeroTitle>

                    <Subtitle>
                        Pohľad našich ľudí na futbal! Všetky cenné rady, informácie, analýzy, predikcie a podcasty, ktoré ti môžu pomôcť posunúť tvoj prehľad na vyššiu úroveň.
                    </Subtitle>


                    <CTAButton onClick={handleCTAClick}>
                        <PlayIcon />
                        Posuň svoju úroveň vyššie
                    </CTAButton>

                    <StatsContainer>
                        <StatItem>
                            <StatIcon>
                                <FaUsers />
                            </StatIcon>
                            <StatNumber>470+</StatNumber>
                            <StatLabel>Analyzovaných tipov</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatIcon>
                                <FaChartLine />
                            </StatIcon>
                            <StatNumber>470x</StatNumber>
                            <StatLabel>Živý pohľad zo zápasu na tikete. Nie si v tom sám!</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatIcon>
                                <FaStar />
                            </StatIcon>
                            <StatNumber>5.0</StatNumber>
                            <StatLabel>Hodnotenie Appstore</StatLabel>
                        </StatItem>
                    </StatsContainer>
                </TextSection>

                <PreviewSection>
                    <AppPreviewContainer>
                        <AppPreviewImage
                            src="/previewapp.png"
                            alt="Náhľad aplikácie - Profesionálna tipovačská platforma"
                        />
                    </AppPreviewContainer>
                </PreviewSection>
            </HeroContent>

            <RegisterModal
                isOpen={isRegisterModalOpen}
                onRequestClose={handleCloseRegisterModal}
                onRegister={handleRegisterSuccess}
            />
        </HeroContainer>
    );
};

export default Hero;